import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import {
  AdminAlertText,
  AdminDetailFieldLabel,
  AdminDetailFieldValue,
  AdminFilterChevron,
  AdminFilterDropdownButton,
  AdminFilterMenuItem,
  AdminHeaderAction,
  AdminLinkAction,
  AdminMetadataText,
  AdminMetric,
  AdminMetricDelta,
  AdminPageTitle,
  AdminPanelTitle,
  AdminRowActionsButton,
  AdminSearchField,
  AdminStatusBadge,
  AdminSupportingText,
  AdminTabButton,
  AdminTableBody,
  AdminTableHeader,
  AdminTableMeta,
  AdminWidgetTitle,
  ADMIN_LIST_FILTER_MENU_CLASS,
  ADMIN_TOOLBAR_CLASS,
  ADMIN_TABLE_CLASS,
  ADMIN_SECTION_OFFSET_CLASS,
  ADMIN_METRIC_PANEL_CLASS,
  ADMIN_CONTENT_PANEL_CLASS,
} from '../shared/adminUx';

import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import useClickOutside from '../../../Hooks/useClickOutside';

const formatMetric = (value) => {
  if (value === null || value === undefined) return '-';
  return value.toLocaleString();
};

const formatDateDisplay = (d) => {
  if (!d) return '';
  const date = new Date(d);
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const renderDelta = (change, labelType = 'previous30') => {
  if (change === null || change === undefined) return null;
  const isIncrease = change >= 0;
  const colorClass = isIncrease ? 'text-freshgreen' : 'text-alertred';
  const arrow = isIncrease ? '▲' : '▼';
  const label = labelType === 'previous30'
    ? (isIncrease ? 'Higher than previous 30 days' : 'Lower than previous 30 days')
    : (isIncrease ? 'Higher than last month' : 'Lower than last month');
  return (
    <div className="flex items-center gap-1 mt-1">
      <span className={`${colorClass} text-[12px] leading-none`}>{arrow}</span>
      <span className={` ${colorClass}`}>{isIncrease ? '+' : ''}{Math.abs(change)}%</span>
      <span className=" text-gray-600 ml-1">{label}</span>
    </div>
  );
};

// Mock data driven by date range (used for widgets on list page; detail page uses same for charts)
export const getReportData = (dateFrom, dateTo) => {
  return {
    casesCreated: 1284,
    casesCreatedChange: 4.2,
    casesClosed: 1128,
    casesClosedChange: 3.1,
    avgResolutionTime: '14.8h',
    avgResolutionTimeChange: -5.2,
    slaComplianceRate: '91.6%',
    slaComplianceRateChange: 1.2,
    contentRemovedRate: '25.4%',
    contentRemovedRateChange: -0.8,
    escalationRate: '6.8%',
    escalationRateChange: -0.3,
    totalCases: 1482,
    actionsTaken: 614,
    escalationsRaised: 72,
    openCases: 19,
    // Case Volume Trend: created vs closed by week (varied for visible ups/downs)
    caseVolumeTrend: [
      { week: 'W1', created: 318, closed: 285 },
      { week: 'W2', created: 295, closed: 312 },
      { week: 'W3', created: 342, closed: 298 },
      { week: 'W4', created: 278, closed: 335 },
      { week: 'W5', created: 365, closed: 302 },
      { week: 'W6', created: 308, closed: 328 },
    ],
    // Outcome Trend: no action / removed / warning / suspension by week (varied)
    outcomeTrend: [
      { week: 'W1', noAction: 168, removed: 48, warning: 58, suspension: 18 },
      { week: 'W2', noAction: 142, removed: 62, warning: 52, suspension: 24 },
      { week: 'W3', noAction: 185, removed: 38, warning: 68, suspension: 15 },
      { week: 'W4', noAction: 155, removed: 55, warning: 48, suspension: 22 },
      { week: 'W5', noAction: 172, removed: 42, warning: 62, suspension: 19 },
      { week: 'W6', noAction: 148, removed: 58, warning: 55, suspension: 21 },
    ],
    // SLA Performance Trend (% by week) – varied for visible movement
    slaPerformanceTrend: [
      { week: 'W1', sla: 88.2 },
      { week: 'W2', sla: 92.5 },
      { week: 'W3', sla: 89.1 },
      { week: 'W4', sla: 94.0 },
      { week: 'W5', sla: 90.8 },
      { week: 'W6', sla: 93.2 },
    ],
    // Source Breakdown: posts / reviews / messages / profiles
    sourceBreakdown: [
      { name: 'Posts', value: 421 },
      { name: 'Reviews', value: 612 },
      { name: 'Messages', value: 303 },
      { name: 'Profiles', value: 146 },
    ],
    writtenSummary: [
      'Moderation volume increased compared to the previous period, driven primarily by review activity.',
      'Escalations remained within expected thresholds.',
      'No systemic moderation issues were identified during this period.',
    ],
  };
};

// System-generated reports list (filtered by date range; latest version only)
const getReportsList = (dateFrom, dateTo) => [
  {
    id: 'MR-2026-01-MS',
    reportName: 'Monthly Moderation Summary',
    reportingPeriod: 'January 2026',
    generatedOn: '2026-02-01',
    type: 'System',
  },
  {
    id: 'MR-2026-W4-D',
    reportName: 'Weekly Moderation Digest',
    reportingPeriod: 'Week 4 - Jan 2026',
    generatedOn: '2026-01-29',
    type: 'System',
  },
  {
    id: 'MR-2026-01-EO',
    reportName: 'Escalation Overview',
    reportingPeriod: 'January 2026',
    generatedOn: '2026-02-01',
    type: 'System',
  },
];

// Grid template: Report Name, Reporting Period, Generated On, Type share space; Actions stays small
const REPORTS_GRID_COLUMNS = 'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) auto';

const ModerationReports = () => {
  const navigate = useNavigate();
  const [dateRangeOpen, setDateRangeOpen] = useState(false);
  const [rowActionsOpen, setRowActionsOpen] = useState({});
  const dateRangeRef = useRef(null);
  const rowActionsRefs = useRef({});
  useClickOutside(dateRangeRef, () => setDateRangeOpen(false));

  // Close row actions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOutsideAll = Object.values(rowActionsRefs.current).every(ref => {
        const el = ref;
        return !el || !el.contains(event.target);
      });
      if (isOutsideAll) setRowActionsOpen({});
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  // Default: Jan 2026
  const [dateFrom, setDateFrom] = useState('2026-01-01');
  const [dateTo, setDateTo] = useState('2026-01-31');
  const [loading, setLoading] = useState(true);

  const dateDisplay = `${formatDateDisplay(dateFrom)} - ${formatDateDisplay(dateTo)}`;

  const reportData = useMemo(() => getReportData(dateFrom, dateTo), [dateFrom, dateTo]);
  const reportsList = useMemo(() => getReportsList(dateFrom, dateTo), [dateFrom, dateTo]);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => setLoading(false), 400);
    return () => clearTimeout(t);
  }, [dateFrom, dateTo]);

  // Presets for date range dropdown
  const setPreset = (preset) => {
    const now = new Date();
    let from, to;
    if (preset === 'jan2026') {
      from = '2026-01-01';
      to = '2026-01-31';
    } else if (preset === 'dec2025') {
      from = '2025-12-01';
      to = '2025-12-31';
    } else if (preset === 'last30') {
      to = new Date(now);
      from = new Date(now);
      from.setDate(from.getDate() - 30);
      from = from.toISOString().slice(0, 10);
      to = to.toISOString().slice(0, 10);
    } else {
      from = dateFrom;
      to = dateTo;
    }
    setDateFrom(from);
    setDateTo(to);
    setDateRangeOpen(false);
  };

  const toggleRowActions = (reportId) => {
    setRowActionsOpen((prev) => {
      if (prev[reportId]) return {};
      return { [reportId]: true };
    });
  };

  const handleViewReport = (reportId) => {
    setRowActionsOpen({});
    navigate(`/admin/reports/${reportId}`);
  };

  const handleExportReport = (row) => {
    setRowActionsOpen({});
    // Export single report (e.g. CSV) — placeholder: trigger download
    const headers = ['Report ID', 'Report Name', 'Reporting Period', 'Generated On', 'Type'];
    const rowData = [
      row.id || '',
      row.reportName || '',
      row.reportingPeriod || '',
      formatDateDisplay(row.generatedOn) || '',
      row.type || '',
    ];
    const csvContent = [headers.join(','), rowData.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `moderation-report-${(row.id || 'export').replace(/\s+/g, '-')}.csv`;
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <AdminSupportingText>Loading moderation reports...</AdminSupportingText>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>
        <AdminPageTitle>Moderation Reports</AdminPageTitle>

        <div className={ADMIN_TOOLBAR_CLASS}>
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center lg:justify-between">
            <div />
            <div className="relative flex-shrink-0" ref={dateRangeRef}>
              <AdminFilterDropdownButton
                type="button"
                onClick={() => setDateRangeOpen(!dateRangeOpen)}
                aria-expanded={dateRangeOpen}
                aria-label="Date Range"
              >
                <span className="truncate">{dateDisplay}</span>
                <AdminFilterChevron open={dateRangeOpen} />
              </AdminFilterDropdownButton>
              {dateRangeOpen && (
                <div className={ADMIN_LIST_FILTER_MENU_CLASS}>
                  <AdminFilterMenuItem
                    active={dateFrom === '2026-01-01' && dateTo === '2026-01-31'}
                    onClick={() => setPreset('jan2026')}
                  >
                    01 Jan 2026 - 31 Jan 2026
                  </AdminFilterMenuItem>
                  <AdminFilterMenuItem
                    active={dateFrom === '2025-12-01' && dateTo === '2025-12-31'}
                    onClick={() => setPreset('dec2025')}
                  >
                    01 Dec 2025 - 31 Dec 2025
                  </AdminFilterMenuItem>
                  <AdminFilterMenuItem
                    active={false}
                    onClick={() => setPreset('last30')}
                  >
                    Last 30 days
                  </AdminFilterMenuItem>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* KPI Widgets */}
        <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 ${ADMIN_SECTION_OFFSET_CLASS}`}>
          <DashboardMainContainer className={ADMIN_METRIC_PANEL_CLASS}>
            <div className="text-sm text-offblack opacity-80 mb-1">Cases Created</div>
            <div className="text-2xl font-semibold text-offblack">{formatMetric(reportData.casesCreated)}</div>
            {renderDelta(reportData.casesCreatedChange)}
          </DashboardMainContainer>
          <DashboardMainContainer className={ADMIN_METRIC_PANEL_CLASS}>
            <div className="text-sm text-offblack opacity-80 mb-1">Cases Closed</div>
            <div className="text-2xl font-semibold text-offblack">{formatMetric(reportData.casesClosed)}</div>
            {renderDelta(reportData.casesClosedChange)}
          </DashboardMainContainer>
          <DashboardMainContainer className={ADMIN_METRIC_PANEL_CLASS}>
            <div className="text-sm text-offblack opacity-80 mb-1">Avg Resolution Time</div>
            <div className="text-2xl font-semibold text-offblack">{reportData.avgResolutionTime}</div>
            {renderDelta(reportData.avgResolutionTimeChange)}
          </DashboardMainContainer>
          <DashboardMainContainer className={ADMIN_METRIC_PANEL_CLASS}>
            <div className="text-sm text-offblack opacity-80 mb-1">SLA Compliance Rate</div>
            <div className="text-2xl font-semibold text-offblack">{reportData.slaComplianceRate}</div>
            {renderDelta(reportData.slaComplianceRateChange)}
          </DashboardMainContainer>
          <DashboardMainContainer className={ADMIN_METRIC_PANEL_CLASS}>
            <div className="text-sm text-offblack opacity-80 mb-1">Content Removed Rate</div>
            <div className="text-2xl font-semibold text-offblack">{reportData.contentRemovedRate}</div>
            {renderDelta(reportData.contentRemovedRateChange)}
          </DashboardMainContainer>
          <DashboardMainContainer className={ADMIN_METRIC_PANEL_CLASS}>
            <div className="text-sm text-offblack opacity-80 mb-1">Escalation Rate</div>
            <div className="text-2xl font-semibold text-offblack">{reportData.escalationRate}</div>
            {renderDelta(reportData.escalationRateChange)}
          </DashboardMainContainer>
        </div>

        {/* Reports table - action menu: View & Export */}
        <DashboardMainContainer className={ADMIN_TABLE_CLASS}>
          <div className="overflow-x-auto">
            {/* Header */}
            <div className="grid py-3 text-offblack border-b border-lightgray px-4 gap-4" style={{ gridTemplateColumns: REPORTS_GRID_COLUMNS }}>
              <div className="flex items-center">
                <span className="font-semibold">Report Name</span>
              </div>
              <div className="flex items-center">
                <span className="font-semibold">Reporting Period</span>
              </div>
              <div className="flex items-center">
                <span className="font-semibold">Generated On</span>
              </div>
              <div className="flex items-center">
                <span className="font-semibold">Type</span>
              </div>
              <div className="flex items-center justify-center">
                <AdminTableHeader>Actions</AdminTableHeader></div>
            </div>
            {/* Rows */}
            {reportsList.map((row) => (
              <div
                key={row.id}
                className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-4 items-center"
                style={{ gridTemplateColumns: REPORTS_GRID_COLUMNS }}
              >
                <div className="flex items-center min-w-0">
                  <span className="truncate">{row.reportName}</span>
                </div>
                <div className="flex items-center">
                  <span className="whitespace-nowrap">{row.reportingPeriod}</span>
                </div>
                <div className="flex items-center">
                  <span className="whitespace-nowrap">{formatDateDisplay(row.generatedOn)}</span>
                </div>
                <div className="flex items-center">
                  <span className="">{row.type}</span>
                </div>
                <div className="flex items-center justify-center relative" onClick={(e) => e.stopPropagation()}>
                  <div className="relative inline-flex" ref={el => rowActionsRefs.current[row.id] = el}>
                    <button
                      type="button"
                      onClick={() => toggleRowActions(row.id)}
                      className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors"
                      title="Actions"
                    >
                      <i className="fi fi-rs-menu-dots-vertical text-offblack flex items-center justify-center" />
                    </button>
                    {rowActionsOpen[row.id] && (
                      <div className="absolute right-0 top-full mt-1 bg-white rounded-lg z-30 min-w-[140px] shadow-lg border border-lightgray">
                        <div className="py-2">
                          <button
                            type="button"
                            onClick={() => handleViewReport(row.id)}
                            className="block px-3 py-1.5 hover:text-primaryblue w-full text-left"
                          >
                            <AdminFilterMenuItem>View</AdminFilterMenuItem>
                          </button>
                          <button
                            type="button"
                            onClick={() => handleExportReport(row)}
                            className="block px-3 py-1.5 hover:text-primaryblue w-full text-left"
                          >
                            <AdminFilterMenuItem>Export</AdminFilterMenuItem>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </DashboardMainContainer>
      </div>
    </AdminLayout>
  );
};

export default ModerationReports;
